import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckableSelectItem, CheckableTagSelectItem, DropdownSelect, Icon, Tag, } from '@dixa/lib-shared-habla';
import unionBy from 'lodash/unionBy';
import xorBy from 'lodash/xorBy';
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { searchApi } from 'apis/search';
import { languages } from 'app/utils/supportedLanguages';
import { VariableScope } from 'components/automations/components/nodeTypes/types';
import { useLazyPaginatedEndpoint, } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import styles from './matchToken.module.scss';
const MatchTokenWrapper = (props) => {
    var _a;
    return (_jsxs("div", Object.assign({ className: styles.root }, { children: [_jsx("div", Object.assign({ className: styles.tokenContainer }, { children: (_a = props.selectedItems) === null || _a === void 0 ? void 0 : _a.map((item) => {
                    return (_jsx(Tag, Object.assign({ color: item.color, onDelete: () => { var _a; return (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, item, false); }, className: styles.token }, { children: item.name }), item.id));
                }) })), _jsx(DropdownSelect, Object.assign({ items: props.items, selectedItems: props.selectedItems, itemComponent: props.itemComponent, pagination: props.pagination, onToggle: props.onToggle, onChange: props.onChange, hasSearch: props.hasSearch, onSearch: props.onSearch, dropdownProps: {
                    style: {
                        width: '275px',
                    },
                }, className: styles.addTokenContainer, buttonProps: { className: styles.addTokenButton }, fixed: true, keepOpen: true }, { children: _jsx(Icon, { name: "dropdown-list", size: 16 }) }))] })));
};
const createStaticMatchTokenComponent = (items) => {
    function MatchToken({ input }) {
        var _a;
        const selectedItems = useMemo(() => {
            var _a, _b;
            return ((_b = (_a = input.value) === null || _a === void 0 ? void 0 : _a.values) === null || _b === void 0 ? void 0 : _b.map((item) => {
                return {
                    id: item.value,
                    name: item.meta.label,
                    color: item.meta.color,
                };
            })) || [];
        }, [(_a = input.value) === null || _a === void 0 ? void 0 : _a.values]);
        const handleChange = useCallback((item, checked) => {
            const newItems = (checked ? unionBy : xorBy)(selectedItems, [item], 'id');
            input.onChange({
                scope: VariableScope.INPUT,
                values: newItems.map((item) => {
                    const meta = { label: item.name };
                    if (item.color) {
                        meta.color = item.color;
                    }
                    return {
                        value: item.id.toString(),
                        meta,
                    };
                }),
            });
        }, [input, selectedItems]);
        return (_jsx(MatchTokenWrapper, { items: items, selectedItems: selectedItems, itemComponent: CheckableSelectItem, onChange: handleChange }));
    }
    return MatchToken;
};
const createDynamicMatchTokenComponent = (options, defaultArg, itemComponent = CheckableSelectItem) => {
    function MatchToken({ input }) {
        var _a;
        const [searchTerm, setSearchTerm] = useState();
        const { items: hits, arg, changeArg, hasNextPage, nextPage, } = useLazyPaginatedEndpoint(Object.assign(Object.assign({}, options), { infinite: true }));
        useEffect(() => {
            if (searchTerm !== (arg === null || arg === void 0 ? void 0 : arg.searchTerm)) {
                changeArg(Object.assign(Object.assign({}, defaultArg), { searchTerm, offset: undefined, limit: undefined }), {
                    debounceTime: 300,
                });
            }
            // Don't rely on arg as dependency because we change the arg inside.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [changeArg, searchTerm]);
        const selectedItems = useMemo(() => {
            var _a, _b;
            return ((_b = (_a = input.value) === null || _a === void 0 ? void 0 : _a.values) === null || _b === void 0 ? void 0 : _b.map((item) => {
                return {
                    id: item.value,
                    name: item.meta.label,
                    color: item.meta.color,
                };
            })) || [];
        }, [(_a = input.value) === null || _a === void 0 ? void 0 : _a.values]);
        const items = useMemo(() => hits.map((hit) => ({
            id: hit.id,
            name: hit.name,
            color: hit.color,
        })), [hits]);
        const handleSearch = useCallback((value) => {
            setSearchTerm(value === null || value === void 0 ? void 0 : value.trim());
        }, []);
        const handleToggle = useCallback((isOpen) => {
            if (isOpen && !hits.length) {
                changeArg(Object.assign(Object.assign({}, defaultArg), { searchTerm, offset: undefined, limit: undefined }), { debounceTime: 0 });
            }
            else if (!isOpen && searchTerm !== '') {
                setSearchTerm('');
            }
        }, [changeArg, hits.length, searchTerm]);
        const handleChange = useCallback((item, checked) => {
            const newItems = (checked ? unionBy : xorBy)(selectedItems, [item], 'id');
            input.onChange({
                scope: VariableScope.INPUT,
                values: newItems.map((item) => {
                    const meta = { label: item.name };
                    if (item.color) {
                        meta.color = item.color;
                    }
                    return {
                        value: item.id.toString(),
                        meta,
                    };
                }),
            });
        }, [input, selectedItems]);
        return (_jsx(MatchTokenWrapper, { items: items, selectedItems: selectedItems, itemComponent: itemComponent, pagination: {
                infinite: true,
                nextEnable: hasNextPage,
                previousEnable: false,
                onNext: () => Promise.resolve(nextPage()),
            }, onToggle: handleToggle, onChange: handleChange, hasSearch: true, onSearch: handleSearch }));
    }
    return MatchToken;
};
export const MatchTokenQueue = createDynamicMatchTokenComponent({
    endpoint: searchApi.endpoints.readSearchQueue,
    itemsSelector: ({ data: { results } }) => { var _a; return (_a = results.hits) !== null && _a !== void 0 ? _a : []; },
}, {
    sort: { key: 'name', direction: 'asc' },
});
export const MatchTokenTags = createDynamicMatchTokenComponent({
    endpoint: searchApi.endpoints.readSearchTag,
    itemsSelector: ({ data: { results } }) => { var _a; return (_a = results.hits) !== null && _a !== void 0 ? _a : []; },
}, {
    sort: { key: 'name', direction: 'asc' },
}, CheckableTagSelectItem);
export const MatchTokenStatus = createStaticMatchTokenComponent([
    { id: 'closed', name: 'Closed' },
    { id: 'pending', name: 'Follow-up' },
    { id: 'open', name: 'Open' },
]);
export const MatchTokenLanguage = createStaticMatchTokenComponent(languages.map((lang) => ({ id: lang.code, name: lang.label })));
